import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { CompanyApi } from '../apis/company-api';
import CloseableErrorMessage from '../components/CloseableErrorMessage';
import { Company } from '../models/company-interface';

export const CompanyQueries = {
  useCompanyDetails: (companyId: number) =>
    useQuery({
      queryKey: ['companyDetails', companyId],
      queryFn: async () => await CompanyApi.getCustomerServiceCompanyDetails(companyId),
      enabled: !!companyId,
    }),

  useCompanyStats: (companyId: number | undefined, daysBack: number, deleted: boolean = true) =>
    useQuery({
      queryKey: ['companyStats', companyId, daysBack],
      queryFn: async () => await CompanyApi.getCompanyStats(companyId!, daysBack),
      enabled: !!companyId && !deleted,
      onError: () => CloseableErrorMessage('Error getting company stats.'),
    }),

  useSupportDetails: (companyId: number | undefined, deleted: boolean = true) =>
    useQuery({
      queryKey: ['supportDetails', companyId],
      queryFn: async () => await CompanyApi.getSupportDetails(companyId!),
      enabled: !!companyId && !deleted,
      onError: () => CloseableErrorMessage('Error getting support details.'),
    }),

  useCompanyDocumentSettings: (companyId: number | undefined, deleted: boolean = true) => {
    return  useQuery({
      queryKey: ['companyDocumentSettings', companyId],
      queryFn: async () => await CompanyApi.getCompanyDocumentSettings(companyId!),
      enabled: !!companyId && !deleted,
      onError: () => CloseableErrorMessage('Error getting company settings.'),
    });
  },

  useCompanyMeetingSettings: (companyId: number | undefined, deleted: boolean = true) => 
    useQuery({
      queryKey: ['companyMeetingSettings', companyId],
      queryFn: async () => await CompanyApi.getCompanyMeetingSettings(companyId!),
      enabled: !!companyId && !deleted,
      onError: () => CloseableErrorMessage('Error getting company settings.'),
    }),

  useCompanyLogo: (companyId: number | undefined, deleted: boolean = true) => 
    useQuery({
      queryKey: ['companyLogo', companyId],
      queryFn: async () => {
        const logo = await CompanyApi.getCompanyLogo(companyId!);
        const base64Str = logo.length ? "data:image/png;base64," + logo : '';
        return base64Str;
      },
      enabled: !!companyId && !deleted,
      onError: () => CloseableErrorMessage('Error getting company logo.'),
    }),

  useUpdateCompanyAccess: (companyId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (isEnabled: boolean) => await CompanyApi.putCompanyAccess({ id: companyId, confirmed: isEnabled } as Company),
      onError: () => CloseableErrorMessage('Error updating company access.'),
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ['companyDetails', companyId]});
        message.success('Updated company access successfully.');
      }
    })
  },

  usePostIndexAll: () => {
    return useMutation({
      mutationFn: async (val: {indexNow: boolean, companyIdList: number[]}) => await CompanyApi.postIndexAll(val.indexNow, val.companyIdList),
      onError: () => CloseableErrorMessage('Error updating company indexers.'),
      onSuccess: () => {
        message.success('Updated company Indexers.');
      }
    })
  },

  usePostIndexAllByEntity: () => {
    return useMutation({
      mutationFn: async (entityIdList: number[]) => await CompanyApi.postIndexAllByEntity(entityIdList),
      onError: () => CloseableErrorMessage('Error updating company indexers for listed entities.'),
      onSuccess: () => {
        message.success('Updated company Indexers for listed entities.');
      }
    })
  },

  usePostIndexFilesBySectionType: () => {
    return useMutation({
      mutationFn: async (sectionTypeIdList: number[]) => await CompanyApi.postIndexFilesBySection(sectionTypeIdList),
      onError: () => CloseableErrorMessage('Error updating company indexers for listed section types.'),
      onSuccess: () => {
        message.success('Updated company Indexers for files of the listed section types.');
      }
    })
  },

  useCompanyParentChildRelations: (companyId: number | undefined, deleted: boolean = true) =>
    useQuery({
      queryKey: ['companyParentChildRelations', companyId],
      queryFn: async () => await CompanyApi.getCompanyParentChildRelations(companyId!),
      enabled: !!companyId && !deleted,
      onError: () => CloseableErrorMessage('Error getting company parent and children.'),
    }),

  useSamlIsEnabledForCompany: (companyId: number | undefined) =>
    useQuery({
      queryKey: ['samlIsEnabledForCompany', companyId],
      queryFn: async () => await CompanyApi.getSamlIsEnabledForCompany(companyId!),
      enabled: !!companyId,
      onError: () => CloseableErrorMessage('Error getting company saml setting.'),
    }),

  useSamlIsConfiguredForCompany: (companyId: number | undefined) =>
    useQuery({
      queryKey: ['samlIsConfiguredForCompany', companyId],
      queryFn: async () => await CompanyApi.getSamlIsConfiguredForCompany(companyId!),
      enabled: !!companyId,
      onError: () => CloseableErrorMessage('Error getting company saml setting.'),
    }),
};
